import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import { showToast } from 'stores/ToastStore';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import classnames from 'classnames';
import SessionStore from 'stores/SessionStore';
import AccountingWebAPIUtils from 'utils/AccountingWebAPIUtils';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import CustomersAPI from 'utils/CustomersAPIUtils';
import PayoutStore from 'stores/accounting/PayoutStore';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import JobList from 'components/modules/accounting/JobList';
import CargomaticCard from 'components/v2/card';
import CargomaticBadge from 'components/v2/Badge';
import _ from 'underscore';
import CircleCheckIcon from 'components/v2/icons/CircleCheckIcon';
import CircleXIcon from 'components/v2/icons/CircleXIcon';
import CargomaticSelect from 'components/v2/select';
import ShipmentSearch from 'components/site/shipment-search';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import { Translation, withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { unescapeSpecialChars } from 'src/main/js/helpers/helper_functions';
import DownloadIcon from '../../../v2/icons/DownloadIcon';
import EmailIcon from '../../../v2/icons/EmailIcon';
import DollarIcon from '../../../v2/icons/DollarIcon';
import PaymentHistory from '../payment-history';
import PaymentSummary from '../payment-summary';
import LogPaymentModal from '../log-payment-modal';
import SendEmailModal from '../send-email-modal';
import { getPayoutStatusColor } from './colors';
import { paymentMethods, paymentStatus, } from '../../../../constants/accounting';
import { getSignedUrl, getDocuments, getDocumentsV2, } from '../../../../utils/DocumentsAPIUtils';
import HistoryTable from '../history-table';
import { sluggify } from '../../../../utils/localization/utils';
const setPayoutIdOnPayoutLineItems = (payoutLineItemGroups, payoutId) => {
    payoutLineItemGroups.forEach((payoutLineItemGroup) => {
        var _a;
        return (_a = payoutLineItemGroup.lineItems) === null || _a === void 0 ? void 0 : _a.forEach(function (lineItem) {
            lineItem.payout_id = payoutId;
        });
    });
};
const getStateFromStores = (id) => {
    const payout = PayoutStore.getPayout(id);
    let balance = 0;
    if (payout) {
        balance = payout.amount_due;
        if (payout.lineItemGroups) {
            setPayoutIdOnPayoutLineItems(payout.lineItemGroups, payout._id);
        }
    }
    return {
        payout: payout,
        balance: balance,
        loading: false,
    };
};
const STRIPE = 'automated';
const ACH = 'manual';
const PAYMENT_METHOD_TOOLTIP_MESSAGE = (React.createElement(Translation, null, (t) => React.createElement(React.Fragment, null, t('APPROVE_PAYOUTS_MSG_FOR_CARRIERS'))));
const INVALID_REPROCESS_STATUS_TOOLTIP = (React.createElement(Tooltip, { id: "tooltip-reprocess-payout" },
    React.createElement(Translation, null, (t) => React.createElement(React.Fragment, null, t('ONLY_INTERNAL_REVIEW_ELIGIBLE')))));
const JOBLIST_ACTIONS = {
    ADD: 'add',
    REMOVE: 'remove',
};
class PayoutDetails extends React.Component {
    constructor(props) {
        super(props);
        this.reprocessCarrierPayout = async () => {
            const { t } = this.props;
            this.setState({ disableReprocessPayoutButton: true }, async () => {
                const { payout: { _id }, } = this.state;
                const response = await AccountingWebAPIUtils.reprocessCarrierPayout(_id).catch((entity) => {
                    const err = entity.error;
                    if (err) {
                        showToast(t('REPROCESS_ERROR'), t('ERROR_REPROCESSING_WITH_VALUE', { error: err }), 'error');
                    }
                });
                const update = { disableReprocessPayoutButton: false };
                if (response && response.payout) {
                    const updatedPayout = Object.assign(Object.assign({}, response.payout), { jobs: response.payout.jobs.map(({ _id }) => _id) });
                    const lineItems = await this.getLineItems(updatedPayout);
                    update.payout = updatedPayout;
                    update.lineItems = lineItems;
                    showToast(t('REPROCESS_SUCCESS'), t('SUCCESSFULLY_REPROCESSED_PAYOUT'), 'success');
                }
                this.setState(update, async () => {
                    // Refresh signed URL after state updates with latest payout
                    await this.updatePayoutLink();
                });
            });
        };
        this.waitForDocument = async (foreignKey, attempt = 0) => {
            var _a, _b;
            const response = await getDocuments([foreignKey], null, {
                limit: 1,
                order: {
                    field: 'createdAt',
                    direction: 'desc',
                },
            });
            if (attempt === 5) {
                return response;
            }
            if (((_b = (_a = response.data.getDocuments) === null || _a === void 0 ? void 0 : _a.documents[0]) === null || _b === void 0 ? void 0 : _b.status) === 'PENDING') {
                await new Promise((resolve) => {
                    setTimeout(() => {
                        resolve();
                    }, 2000);
                });
                return this.waitForDocument(foreignKey, attempt + 1);
            }
            return response;
        };
        this.updatePayoutLink = async () => {
            var _a, _b, _c, _d;
            const payoutState = this.state.payout;
            const { data: documentPayout, errors: documentErrors } = await this.waitForDocument(payoutState._id);
            if (documentErrors) {
                this.showGetDocumentsErrorToast(documentErrors[0].message);
            }
            const key = (_c = (_b = (_a = documentPayout === null || documentPayout === void 0 ? void 0 : documentPayout.getDocuments) === null || _a === void 0 ? void 0 : _a.documents) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.key;
            if (!key) {
                const lineItems = await this.getLineItems(this.state.payout);
                this.setState({
                    linkToPayout: undefined,
                    lineItems,
                });
                return;
            }
            const { data, errors } = await getSignedUrl(key);
            if (errors) {
                this.showGetSignedUrlErrorToast(errors[0].message);
            }
            const linkToPayout = (_d = data === null || data === void 0 ? void 0 : data.getSignedUrl) === null || _d === void 0 ? void 0 : _d.signedUrl;
            const lineItems = await this.getLineItems(this.state.payout);
            this.setState({
                linkToPayout,
                lineItems,
            });
        };
        this.getLineItems = async (payout) => {
            try {
                const lineItemsResult = await ShipmentWebAPIUtils.getShipmentsLedgers(payout.jobs, 'payout').catch((e) => console.log('Error: ', e));
                const jobLineItems = payout.lineItemGroups.map((job) => {
                    return {
                        _id: job.shipment_id,
                        payables: job.lineItems.map((lineItem) => (Object.assign(Object.assign({}, lineItem), { payout_id: payout._id }))),
                    };
                });
                return payout.status === 'paid'
                    ? jobLineItems
                    : lineItemsResult.items || [];
            }
            catch (e) {
                console.log('Get Line Items Error: ', e);
            }
        };
        this.showGetDocumentsErrorToast = (err) => {
            const { t } = this.props;
            showToast(t('GET_DOCUMENT_ERROR'), t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: err }), 'error');
        };
        this.showGetSignedUrlErrorToast = (err) => {
            const { t } = this.props;
            showToast(t('GET_SIGNED_URL_ERROR'), t('GET_SIGNED_URL_FAILED_WITH_ERROR', { error: err }), 'error');
        };
        this.onChange = async () => {
            var _a, _b, _c;
            const payoutState = getStateFromStores(this.getPayoutId());
            const { data: documentPayout, errors: documentErrors } = await getDocumentsV2([payoutState.payout._id], null, {
                limit: 1,
                order: {
                    field: 'createdAt',
                    direction: 'desc',
                },
            });
            if (documentErrors) {
                this.showGetDocumentsErrorToast(documentErrors[0].message);
            }
            const document = (_b = (_a = documentPayout === null || documentPayout === void 0 ? void 0 : documentPayout.getDocumentsV2) === null || _a === void 0 ? void 0 : _a.documents) === null || _b === void 0 ? void 0 : _b[0];
            if (document === null || document === void 0 ? void 0 : document.key) {
                const { data, errors } = await getSignedUrl(document.key);
                if (errors) {
                    this.showGetSignedUrlErrorToast(errors[0].message);
                }
                else {
                    const linkToPayout = (_c = data === null || data === void 0 ? void 0 : data.getSignedUrl) === null || _c === void 0 ? void 0 : _c.signedUrl;
                    payoutState.linkToPayout = linkToPayout;
                }
            }
            payoutState.disableReprocessPayoutButton =
                payoutState.payout.status !== 'internal_review';
            const lineItems = await this.getLineItems(payoutState.payout);
            this.setState(Object.assign(Object.assign({}, payoutState), { lineItems }));
        };
        this.handlePaymentMethodSelect = (data) => {
            const { name } = data;
            if (!name) {
                return;
            }
            const method = name.toLowerCase();
            if (method && method !== this.state.paymentMethod) {
                this.setState({ paymentMethod: method });
                const method_value = method === 'manual/ach' ? ACH : STRIPE;
                AccountingWebAPIUtils.setPayoutPaymentMethod(method_value, this.state.payout._id);
            }
        };
        this.setChanges = (updates) => {
            const { editMode } = this.state;
            this.setState({ updates });
            if (!editMode) {
                this.setState({ editMode: true });
            }
        };
        this.handleEditButton = async () => {
            var _a, _b, _c;
            const payoutState = getStateFromStores(this.getPayoutId());
            const { editMode, payout, updates } = this.state;
            if (!editMode) {
                return this.setState({ editMode: true });
            }
            if (!updates.changes || !updates.changes.length) {
                return this.setState({ editMode: false });
            }
            if (updates.changes && updates.changes.length) {
                try {
                    const updatedPayout = await AccountingWebAPIUtils.updatePayoutLineItems(payout._id, payout.version, updates.changes);
                    const { data: documentPayout, errors: documentErrors } = await getDocumentsV2([payoutState.payout._id], {
                        limit: 1,
                        order: {
                            field: 'createdAt',
                            direction: 'desc',
                        },
                    });
                    if (documentErrors) {
                        this.showGetDocumentsErrorToast(documentErrors[0].message);
                        return;
                    }
                    const { data, errors } = await getSignedUrl((_b = (_a = documentPayout.getDocumentsV2) === null || _a === void 0 ? void 0 : _a.documents[0]) === null || _b === void 0 ? void 0 : _b.key);
                    if (errors) {
                        this.showGetSignedUrlErrorToast(errors[0].message);
                        return;
                    }
                    const linkToPayout = (_c = data === null || data === void 0 ? void 0 : data.getSignedUrl) === null || _c === void 0 ? void 0 : _c.signedUrl;
                    const lineItems = await this.getLineItems(updatedPayout);
                    this.setState({
                        editMode: false,
                        linkToPayout,
                        payout: updatedPayout,
                        updates: [],
                        lineItems,
                    });
                }
                catch (e) {
                    console.log(e);
                }
            }
        };
        this.handleJobSelect = async (payout, job) => {
            var _a, _b, _c;
            const payoutState = getStateFromStores(this.getPayoutId());
            if (payout.jobs.includes(job._id)) {
                return;
            }
            if (payout.status === 'paid' || payout.status === 'payment_initialized') {
                return;
            }
            this.setState({ showLineItemConfirmModal: false });
            const updatedJobIds = [...payout.jobs, job._id];
            const newPayout = await AccountingWebAPIUtils.updatePayoutJobs(payout, updatedJobIds, JOBLIST_ACTIONS.ADD);
            const { data: documentPayout, errors: documentErrors } = await getDocumentsV2([payoutState.payout._id], {
                limit: 1,
                order: {
                    field: 'createdAt',
                    direction: 'desc',
                },
            });
            if (documentErrors) {
                this.showGetDocumentsErrorToast(documentErrors[0].message);
                return;
            }
            const { data, errors } = await getSignedUrl((_b = (_a = documentPayout.getDocumentsV2) === null || _a === void 0 ? void 0 : _a.documents[0]) === null || _b === void 0 ? void 0 : _b.key);
            if (errors) {
                this.showGetSignedUrlErrorToast(errors[0].message);
                return;
            }
            const linkToPayout = (_c = data === null || data === void 0 ? void 0 : data.getSignedUrl) === null || _c === void 0 ? void 0 : _c.signedUrl;
            const lineItems = await this.getLineItems(newPayout);
            this.setState({ payout: newPayout, lineItems, linkToPayout });
        };
        this.handleJobRemove = async (payout, lineItem) => {
            var _a, _b, _c;
            const payoutState = getStateFromStores(this.getPayoutId());
            const { t } = this.props;
            try {
                const updatedJobIds = payout.jobs.filter((id) => id !== lineItem.shipment_id);
                this.setState({ showLineItemConfirmModal: false });
                const newPayout = await AccountingWebAPIUtils.updatePayoutJobs(payout, updatedJobIds, JOBLIST_ACTIONS.REMOVE);
                showToast(t('JOB_REMOVE_SUCCESS'), t('SUCCESSFULLY_REMOVED_JOB'), 'success');
                const { data: documentPayout, errors: documentErrors } = await getDocumentsV2([payoutState.payout._id]);
                if (documentErrors) {
                    this.showGetDocumentsErrorToast(documentErrors[0].message);
                    return;
                }
                const { data, errors } = await getSignedUrl((_b = (_a = documentPayout.getDocumentsV2) === null || _a === void 0 ? void 0 : _a.documents[0]) === null || _b === void 0 ? void 0 : _b.key);
                if (errors) {
                    this.showGetSignedUrlErrorToast(errors[0].message);
                    return;
                }
                const linkToPayout = (_c = data === null || data === void 0 ? void 0 : data.getSignedUrl) === null || _c === void 0 ? void 0 : _c.signedUrl;
                const lineItems = await this.getLineItems(newPayout);
                this.setState({
                    payout: newPayout,
                    lineItems,
                    confirmModal: this.confirmModalDefault(),
                    linkToPayout,
                });
            }
            catch (e) {
                showToast(t('JOB_REMOVE_ERROR'), `${e.entity.error}`, 'error');
            }
        };
        this.handleSearch = async (payout, value, job) => {
            const listOfJobIdsAssociated = [...payout.lineItemGroups];
            const searchedShipmentId = job.shipmentId;
            let listOfJobs = [];
            listOfJobs = _.filter(listOfJobIdsAssociated, (jobsAssociated) => jobsAssociated.shipmentId === searchedShipmentId);
            this.setState({ selectedValue: value });
            if (listOfJobs.length) {
                payout.lineItemGroups = listOfJobs;
                this.setState({ payout: payout });
            }
        };
        this.onSelectChange = async (value) => {
            const payoutState = await getStateFromStores(this.getPayoutId());
            const { selectedValue } = this.state;
            const lineItemGroups = payoutState.payout.lineItemGroups;
            value = value.trim().toUpperCase();
            if (value.length && value !== selectedValue) {
                let listOfJobs = [];
                listOfJobs = _.filter(lineItemGroups, (lineItemGroup) => {
                    if (lineItemGroup.shipmentId.includes(value))
                        return lineItemGroup;
                });
                if (listOfJobs.length) {
                    payoutState.payout.lineItemGroups = listOfJobs;
                    this.setState({ payout: payoutState.payout });
                }
                else {
                    payoutState.payout.lineItemGroups = [];
                    this.setState({ payout: payoutState.payout });
                }
            }
            else {
                this.setState(getStateFromStores(this.getPayoutId()));
            }
        };
        this.confirmModalDefault = () => {
            return {
                title: '',
                message: '',
                onConfirm: null,
            };
        };
        this.confirmAddShipment = async (job) => {
            try {
                const { t } = this.props;
                const ledgersResult = await ShipmentWebAPIUtils.getShipmentsLedgers([job._id], 'payout').catch((e) => {
                    throw e;
                });
                const jobLineItems = ledgersResult && ledgersResult.items && ledgersResult.items.length
                    ? ledgersResult.items[0]
                    : {};
                const hasOtherPaidItems = (jobLineItems.payables || []).some((lineItem) => {
                    if (!lineItem.payout_id) {
                        return false;
                    }
                    return (lineItem.payout &&
                        lineItem.payout.status !== 'paid' &&
                        lineItem.payout._id.toString() !== this.state.payout._id.toString());
                });
                const hasPaidLineItemsEligibleToMove = jobLineItems.some((lineItem) => {
                    if (!lineItem.invoice_id) {
                        return true;
                    }
                    return lineItem.payout && lineItem.payout.status !== 'paid';
                });
                const hasUnpaidLineItems = job.payables.some((payable) => !payable.payout_id);
                if (!hasPaidLineItemsEligibleToMove && !hasUnpaidLineItems) {
                    const title = t('UNABLE_TO_ADD_SHIPMENT_TO_PAYOUT_TITLE', {
                        shipmentId: job.shipment_id,
                    });
                    const message = t('UNABLE_TO_ADD_SHIPMENT_TO_PAYOUT_MESSAGE');
                    this.setState({
                        showLineItemConfirmModal: true,
                        confirmModal: {
                            title,
                            message,
                            onConfirm: () => this.setState({
                                showLineItemConfirmModal: false,
                                confirmModal: this.confirmModalDefault(),
                            }),
                        },
                    });
                }
                else if (hasOtherPaidItems || hasUnpaidLineItems) {
                    const title = t('ADD_SHIPMENT_TO_PAYOUT_TITLE', {
                        shipmentId: job.shipment_id,
                    });
                    const message = t('ADD_SHIPMENT_TO_PAYOUT_MESSAGE');
                    this.setState({
                        showLineItemConfirmModal: true,
                        confirmModal: {
                            title,
                            message,
                            onConfirm: () => this.handleJobSelect(this.state.payout, job),
                        },
                    });
                }
                else {
                    this.handleJobSelect(this.state.payout, job);
                }
            }
            catch (error) {
                console.error(error);
            }
        };
        this.confirmRemoveShipment = (job) => {
            const { t } = this.props;
            const title = t('REMOVE_SHIPMENT_TO_PAYOUT_TITLE', {
                shipmentId: job.shipmentId,
            });
            const message = t('REMOVE_SHIPMENT_TO_PAYOUT_MESSAGE');
            this.setState({
                showLineItemConfirmModal: true,
                confirmModal: {
                    title,
                    message,
                    onConfirm: () => this.handleJobRemove(this.state.payout, job),
                },
            });
        };
        this.state = this.getInitialState();
    }
    getInitialState() {
        return {
            loading: true,
            showSendEmailModal: false,
            disableReprocessPayoutButton: false,
            selectedValue: '',
            paymentMethod: null,
            isAdmin: SessionStore.isAdmin(),
            lineItems: [],
            editMode: false,
            updates: [],
            showLineItemConfirmModal: false,
            confirmModal: this.confirmModalDefault(),
            isFinanceAdmin: SessionStore.isFinanceAdmin(),
            hasFinanceAdminRole: SessionStore.hasFinanceAdminRole(),
        };
    }
    componentDidMount() {
        PayoutStore.register(this.onChange);
        AccountingActionCreators.getPayout(this.getPayoutId());
    }
    componentWillUnmount() {
        PayoutStore.unregister(this.onChange);
    }
    UNSAFE_componentWillUpdate(_nextProps, nextState) {
        if (!this.state.payout && nextState.payout) {
            this.populateFundingData(nextState.payout);
        }
    }
    populateFundingData({ carrier: { _id: carrierId } }) {
        CustomersAPI.getBankAccounts(carrierId)
            .then((res) => {
            this.setState((prevState) => {
                const payout = Object.assign({}, prevState.payout);
                const response = res.entity.data;
                payout.carrier.bank_accounts = response.external_accounts;
                return {
                    payout,
                };
            });
        })
            .catch((err) => {
            console.log(err.stack || err);
        });
    }
    getPayoutId() {
        return this.props.payoutId || this.props.match.params.id;
    }
    populateJobPayablesWithLineItemsFromPayout(jobs, lineItemGroups) {
        return jobs.map((job) => {
            const payoutLineItemsForJob = lineItemGroups.find((payoutJob) => payoutJob.shipment_id === job._id);
            job.payables = (payoutLineItemsForJob === null || payoutLineItemsForJob === void 0 ? void 0 : payoutLineItemsForJob.lineItems) || [];
            return job;
        });
    }
    sendEmail() {
        return (to) => {
            AccountingActionCreators.emailPayout(this.state.payout._id, to);
        };
    }
    savePayment() {
        return (payment) => {
            AccountingActionCreators.recordPaymentForPayout(this.state.payout._id, payment);
        };
    }
    defineDropdownSelectedValue() {
        const { paymentMethod, payout: { payment_method }, } = this.state;
        if (paymentMethod) {
            return paymentMethod === 'automated' ? STRIPE : ACH;
        }
        if (payment_method) {
            return payment_method === 'automated' ? STRIPE : ACH;
        }
        return null;
    }
    render() {
        const { t } = this.props;
        if (this.state.loading) {
            return (React.createElement("p", { "data-testid": "p-8d6c1304", className: "px-4 py-5 sm:p-6" }, t('LOADING')));
        }
        return this.renderPayout();
    }
    approvePayout() {
        return () => {
            this.setState({ showApprovalModal: false }, () => {
                AccountingActionCreators.approvePayout(this.state.payout._id);
            });
        };
    }
    reverseVoid() {
        return () => {
            this.setState({ showVoidModal: false }, () => {
                AccountingActionCreators.reversePayoutVoid(this.state.payout._id);
            });
        };
    }
    voidPayout() {
        return () => {
            this.setState({ showVoidModal: false }, () => {
                AccountingActionCreators.voidPayout(this.state.payout._id);
            });
        };
    }
    reverseApproval() {
        return () => {
            this.setState({ showApprovalModal: false }, () => {
                AccountingActionCreators.reversePayoutApproval(this.state.payout._id);
            });
        };
    }
    generateApprovalButton(payout, action, onConfirm) {
        const { t } = this.props;
        return (React.createElement("div", { "data-testid": "div-97663b50" },
            React.createElement("button", { "data-testid": "button-b044db6d", className: "button button-green-light button-with-icon", onClick: () => this.setState({ showApprovalModal: true }) },
                React.createElement(CircleCheckIcon, null),
                React.createElement("span", { "data-testid": "span-818d03f7" }, t(sluggify(action)))),
            React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-c0408354", show: this.state.showApprovalModal, title: t('VOID_MODAL_WITH_VALUE', {
                    action: t(sluggify(action)),
                    payoutID: payout.number,
                }), onConfirm: onConfirm, onHide: () => this.setState({ showApprovalModal: false }), message: t('ARE_YOU_SURE'), hideButtonText: t('CANCEL'), affirmative: t('OK') })));
    }
    generateVoidButton(payout, action, onConfirm) {
        const { t } = this.props;
        return (React.createElement("div", { "data-testid": "div-9fe1c414" },
            React.createElement("button", { "data-testid": "button-2f50da3c", className: "void-payout button button-red-light button-with-icon", onClick: () => this.setState({ showVoidModal: true }) },
                React.createElement(CircleXIcon, null),
                React.createElement("span", { "data-testid": "span-56b91a01" }, t(sluggify(action)))),
            React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-12c14055", show: this.state.showVoidModal, title: t('VOID_MODAL_WITH_VALUE', {
                    action: t(sluggify(action)),
                    payoutID: payout.number,
                }), onConfirm: onConfirm, onHide: () => this.setState({ showVoidModal: false }), message: t('ARE_YOU_SURE'), hideButtonText: t('CANCEL'), affirmative: t('OK') })));
    }
    renderApprovalButton(payout) {
        const { status } = payout;
        if (status === 'payment_initialized' ||
            status === 'paid' ||
            status === 'void') {
            return '';
        }
        if (status === 'approved_internally') {
            return this.generateApprovalButton(payout, 'Reverse Approval', this.reverseApproval());
        }
        return this.generateApprovalButton(payout, 'Approve', this.approvePayout());
    }
    renderVoidButton(payout) {
        const { status } = payout;
        if (status === 'approved_internally' ||
            status === 'payment_initialized' ||
            status === 'paid') {
            return '';
        }
        if (status === 'void') {
            return this.generateVoidButton(payout, 'Reverse Void', this.reverseVoid());
        }
        return this.generateVoidButton(payout, 'Void', this.voidPayout());
    }
    canMakePayment(payout) {
        return (payout.status === 'approved_internally' ||
            payout.status === 'payment_initialized');
    }
    renderPaymentButtons() {
        const { t } = this.props;
        const { payout } = this.state;
        if (!this.canMakePayment(payout)) {
            return null;
        }
        return (React.createElement("div", { "data-testid": "div-7c649ea2", className: "flex items-center space-x-2" },
            React.createElement("button", { "data-testid": "button-42a3d745", className: "button button-green button-with-icon", onClick: () => this.setState({ showLogPaymentModal: true }), disabled: this.state.balance === 0 },
                React.createElement(DollarIcon, null),
                React.createElement("span", { "data-testid": "span-380f80dd" }, t('RECORD_PAYMENT'))),
            this.state.showLogPaymentModal && (React.createElement(LogPaymentModal, { show: this.state.showLogPaymentModal, amount: (this.state.balance || 0).toFixed(2), onSave: this.savePayment(), onHide: () => this.setState({ showLogPaymentModal: false }) }))));
    }
    renderDownloadButton() {
        const { t } = this.props;
        if (!this.state.linkToPayout) {
            return '';
        }
        return (React.createElement("div", { "data-testid": "div-cbfb50ae" },
            React.createElement("a", { "data-testid": "a-6ee980c1", href: this.state.linkToPayout, target: "_blank", rel: "noopener noreferrer", className: "button button-gray button-with-icon" },
                React.createElement(DownloadIcon, null),
                React.createElement("span", { "data-testid": "span-3857bdc8" }, t('PAYOUT', { count: 1 })))));
    }
    renderEmailButton() {
        const { t } = this.props;
        return (React.createElement("button", { "data-testid": "button-a52cb021", className: "button button-gray button-with-icon", onClick: () => {
                this.setState({ showSendEmailModal: true });
            } },
            React.createElement(EmailIcon, null),
            React.createElement("span", { "data-testid": "span-6e1e35c5" }, titleize(t('EMAIL')))));
    }
    allowPaymentSelection() {
        const { hasFinanceAdminRole, payout: { status, carrier: { stripe_account_id }, }, } = this.state;
        return (hasFinanceAdminRole &&
            stripe_account_id &&
            (status === paymentStatus.APPROVED_INTERNALLY ||
                status === paymentStatus.INTERNAL_REVIEW));
    }
    renderPayout() {
        const { payout, isAdmin, confirmModal: { title, message, onConfirm }, } = this.state;
        const { lineItemGroups } = payout;
        const { t } = this.props;
        const pod_message = payout.missing_pods
            ? t('WITHOUT_PODS_JOBS')
            : t('ALL_PODS_RECEIVED');
        const podBadgeColor = payout.missing_pods ? 'red' : 'green';
        // const display = _.has(payout, 'missing_pods') ? {} : { display: 'none' };
        const reprocessPayoutClass = classnames({
            button: true,
            'button-red': true,
            'button-with-icon': true,
            'pointer-events-none': this.state.disableReprocessPayoutButton,
        });
        return (React.createElement("div", { "data-testid": "div-2dd250b7", className: "mx-auto my-8 sm:my-36 xl:my-8 max-w-screen-2xl" },
            React.createElement("div", { "data-testid": "div-ebb322ba", className: "flex" },
                React.createElement("div", { "data-testid": "div-114cd370", className: "flex w-2/5" },
                    React.createElement("div", { "data-testid": "div-bee8b5e6" },
                        React.createElement("h2", { "data-testid": "h2-bede8f08", className: "my-0 mb-1" }, payout.number),
                        React.createElement("span", { "data-testid": "span-08376061", className: "text-lg text-gray-500" }, t('DUE_DATE_WITH_VALUE', {
                            dueDate: moment(payout.due_date).format('L'),
                        }))),
                    React.createElement("div", { "data-testid": "div-f7adf3ec", className: "flex py-2 ml-2 space-x-2" },
                        React.createElement(CargomaticBadge, { dataTestId: "CargomaticBadge-1a4939eb", color: podBadgeColor }, pod_message),
                        React.createElement(CargomaticBadge, { dataTestId: "CargomaticBadge-4b4cde3d", color: getPayoutStatusColor(payout.status) }, t(sluggify(payout.status))))),
                React.createElement("div", { "data-testid": "div-40433ad6", className: "flex justify-end w-2/3 items-end mb-9" }, isAdmin && (React.createElement("div", { "data-testid": "div-402caed2", className: "px-2" },
                    React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-383b527d", items: paymentMethods, onSelect: this.handlePaymentMethodSelect, label: t('PAYMENT_METHOD'), disabled: !this.allowPaymentSelection(), caption: t('SELECT_PAYMENT_METHOD'), valueKey: "value", textKey: "localizationSlug", selected: this.defineDropdownSelectedValue(), info: PAYMENT_METHOD_TOOLTIP_MESSAGE }))))),
            React.createElement("div", { "data-testid": "div-57bd0464", className: "w-full lg:grid lg:grid-cols-3 lg:gap-4" },
                React.createElement("div", { "data-testid": "div-8e96762b", className: "lg:max-h-96 py-4 overflow-y-auto" },
                    React.createElement(CargomaticCard, { dataTestId: "CargomaticCard-9dc2f9b3", header: () => (React.createElement("div", { "data-testid": "div-dfadca73" },
                            React.createElement("div", { "data-testid": "div-e1f0b576", className: "flex w-full" },
                                React.createElement("div", { "data-testid": "div-5215d4eb", className: "w-1/2" },
                                    React.createElement("h3", { "data-testid": "h3-b0498907", className: "my-0" }, t('PAYOUT_CARRIER_NAME_WITH_VALUE', {
                                        carrierName: unescapeSpecialChars(payout.carrier.name),
                                    }))),
                                React.createElement(OverlayTrigger, { placement: "top", overlay: INVALID_REPROCESS_STATUS_TOOLTIP },
                                    React.createElement("div", { "data-testid": "div-363ef347", className: "flex justify-end w-1/2" },
                                        React.createElement("button", { "data-testid": "button-d8ab3b86", className: reprocessPayoutClass, onClick: this.reprocessCarrierPayout, disabled: this.state.disableReprocessPayoutButton },
                                            React.createElement(CircleCheckIcon, null),
                                            React.createElement("span", { "data-testid": "span-d2144b44" }, t('REPROCESS_PAYOUT')))))))) },
                        payout.carrier.phone && (React.createElement("div", { "data-testid": "div-7f3a0641", className: "mb-6 card--no-x-padding card--no-top-padding" },
                            React.createElement("div", { "data-testid": "div-5148e14a", className: "px-4 py-4 sm:px-6 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4" },
                                React.createElement("dt", { "data-testid": "dt-e14ad00a", className: "text-sm font-medium text-gray-500" }, titleize(t('PHONE'))),
                                React.createElement("dd", { "data-testid": "dd-0cbb7fb3", className: "" }, payout.carrier.phone)))),
                        React.createElement("div", { "data-testid": "div-7d5044b2", className: "mb-4 card--no-x-padding card--no-top-padding" },
                            React.createElement(PaymentSummary, { total: payout.total, paid: payout.total_payments, balance: payout.amount_due, currency: payout.currency })),
                        React.createElement("div", { "data-testid": "div-704841dd", className: "flex flex-wrap items-center mt-4 space-x-2" },
                            this.renderApprovalButton(payout),
                            this.renderVoidButton(payout),
                            React.createElement(AuthorizationWrapper, { excludedRegions: [REGIONS.AP] }, this.renderEmailButton()),
                            this.renderDownloadButton(),
                            React.createElement("a", { "data-testid": "a-566c3eb2", href: `/payouts/${payout._id}`, target: "_blank", rel: "noopener noreferrer", className: "inline-block button button-gray button-with-icon" }, t('OPEN_IN_NEW_TAB'))))),
                React.createElement("div", { "data-testid": "div-b076ea7a", className: "lg:max-h-96 py-4 overflow-y-auto" },
                    React.createElement(CargomaticCard, { dataTestId: "CargomaticCard-a7f622de", header: () => (React.createElement("div", { "data-testid": "div-0f57e797", className: "flex items-center justify-between" },
                            React.createElement("h3", { "data-testid": "h3-96abc8e5", className: "my-0" }, t('PAYMENTS')),
                            this.renderPaymentButtons())), className: "mb-6" },
                        React.createElement("div", { "data-testid": "div-fde64771", className: "-mb-4 card--no-x-padding card--no-top-padding" },
                            React.createElement(PaymentHistory, { payments: payout.payments, currency: payout.currency })))),
                React.createElement("div", { "data-testid": "div-eb15461d", className: "lg:max-h-96 py-4 overflow-y-auto" },
                    React.createElement(CargomaticCard, { dataTestId: "CargomaticCard-94c2c5ee", header: () => (React.createElement("h3", { "data-testid": "h3-245061a4", className: "my-0" }, t('AUDIT_HISTORY'))) },
                        React.createElement("div", { "data-testid": "div-6d52196e", className: "-mb-4 card--no-x-padding card--no-top-padding" },
                            React.createElement(HistoryTable, { data: payout.audit || [], type: "Payout" })))),
                React.createElement(SendEmailModal, { show: this.state.showSendEmailModal, onSend: this.sendEmail(), onHide: () => {
                        this.setState({ showSendEmailModal: false });
                    } })),
            React.createElement("div", { "data-testid": "div-cea48938", className: "py-4" },
                React.createElement(CargomaticCard, { dataTestId: "CargomaticCard-af31b27e", header: () => {
                        return (React.createElement("div", { "data-testid": "div-9e19c771", className: "flex items-center justify-between" },
                            React.createElement("div", { "data-testid": "div-f6477618", className: "flex items-center space-x-2" },
                                React.createElement("h3", { "data-testid": "h3-d79fd21e", className: "my-0" }, t('JOBS')),
                                React.createElement("div", { "data-testid": "div-16983a25", className: "w-60" },
                                    React.createElement(ShipmentSearch, { isFilter: true, lineItemGroups: lineItemGroups, onSelect: (value, job) => {
                                            this.handleSearch(payout, value, job);
                                        }, onChange: (value) => this.onSelectChange(value), disabled: !this.state.isFinanceAdmin })))));
                    } },
                    React.createElement("div", { "data-testid": "div-f6e8f838", className: "-mb-4 card--no-x-padding card--no-top-padding" },
                        React.createElement(JobList, { context: "payout", ledgerType: payout.type, ledgerId: payout._id, version: payout.version, jobs: payout.lineItemGroups, lineItems: this.populateJobPayablesWithLineItemsFromPayout(this.state.lineItems, payout.lineItemGroups), onRemove: this.confirmRemoveShipment, editMode: this.state.editMode, setChanges: this.setChanges, paid: payout.status === 'paid', hideButtons: !this.state.editMode || payout.status !== 'internal_review' })))),
            React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-af080161", show: this.state.showLineItemConfirmModal, title: title, message: message, onConfirm: onConfirm, onHide: () => this.setState({ showLineItemConfirmModal: false }) })));
    }
}
PayoutDetails.propTypes = {
    match: PropTypes.object,
};
export default withTranslation()(PayoutDetails);
